/**
 * Predictive Staffing Model — workload trends, capacity projections, headcount planning.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface MonthlyWorkload {
  month: string; tickets: number; calls: number;
  totalWorkItems: number; workItemsPerAnalystPerDay: number;
}

interface Projection {
  threshold: number; monthsUntilBreach: number | null;
  breachDate: string; status: 'exceeded' | 'warning' | 'critical' | 'safe';
}

interface HeadcountRec {
  threshold: number; currentHeadcount: number; neededNow: number;
  neededNextQuarter: number; needed6Months: number; surplusDeficit: number;
}

interface StaffingData {
  teamSize: number;
  currentState: {
    workItemsPerAnalystPerDay: number; lastFullMonthItems: number;
    monthlyGrowthRate: number; growthPercent: number;
  };
  monthlyWorkload: MonthlyWorkload[];
  projections: Projection[];
  headcountRecommendations: HeadcountRec[];
  seasonalFactors: Array<{ month: string; factor: number; aboveAverage: boolean }>;
  employeeMetrics: { totalEmployees: number; ticketsPerEmployee: number | null; supportRatio: string | null };
  workloadBreakdown: { generalSupport: number; newHires: number; terminations: number; computerDeploys: number; ofSwaps: number } | null;
  generatedAt: string;
}

export function StaffingModel() {
  const [threshold, setThreshold] = useState(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ['staffing-model', threshold],
    queryFn: () => apiClient.get<StaffingData>(`staffing-model?threshold=${threshold}`),
  });

  if (isLoading) return <LoadingSpinner message="Building staffing model..." />;
  if (error) return <ErrorMessage message={(error as Error).message} />;
  if (!data) return null;

  const { teamSize, currentState, monthlyWorkload, projections, headcountRecommendations, seasonalFactors, employeeMetrics, workloadBreakdown } = data;

  // Find the most relevant projection (user's threshold)
  const primaryProjection = projections.find(p => p.threshold === threshold);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Predictive Staffing Model</h2>
          <p className="text-sm text-slate-500">Workload trend analysis and headcount projections for the {teamSize}-person service desk team</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500">Capacity threshold:</label>
          <input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(Math.max(5, Math.min(100, Number(e.target.value) || 30)))}
            min={5}
            max={100}
            className="py-1.5 px-2 rounded-lg border border-border text-sm w-16 text-center font-medium"
          />
          <span className="text-xs text-slate-400">items/person/day</span>
        </div>
      </div>

      {/* ═══ Current State KPIs ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Kpi label="Work Items/Person/Day" value={currentState.workItemsPerAnalystPerDay} color={currentState.workItemsPerAnalystPerDay > 20 ? 'red' : undefined} />
        <Kpi label="Monthly Growth" value={currentState.monthlyGrowthRate > 0 ? `+${currentState.monthlyGrowthRate}` : `${currentState.monthlyGrowthRate}`} sub={`${currentState.growthPercent}%/mo`} color={currentState.growthPercent > 5 ? 'red' : undefined} />
        <Kpi label="Last Full Month" value={currentState.lastFullMonthItems.toLocaleString()} sub="work items" />
        <Kpi label="Team Size" value={teamSize} sub="analysts" />
        <Kpi label="Support Ratio" value={employeeMetrics.supportRatio ?? '—'} sub={`${employeeMetrics.totalEmployees} employees`} />
        <Kpi label="Tickets/Employee/Mo" value={employeeMetrics.ticketsPerEmployee?.toFixed(1) ?? '—'} />
      </div>

      {/* ═══ Capacity Projections ═══ */}
      <div className="bg-white rounded-lg border border-border p-5 mb-6">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Capacity Breach Projections</h3>
        <p className="text-xs text-slate-500 mb-4">When will workload exceed capacity at various thresholds? Based on linear trend of {monthlyWorkload.length} months of data.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {projections.map(p => (
            <div key={p.threshold} className={`rounded-lg p-4 text-center border ${
              p.status === 'exceeded' ? 'bg-red-50 border-red-200' :
              p.status === 'critical' ? 'bg-red-50 border-red-200' :
              p.status === 'warning' ? 'bg-amber-50 border-amber-200' :
              'bg-emerald-50 border-emerald-200'
            }`}>
              <div className="text-xs text-slate-500 mb-1">{p.threshold} items/person/day</div>
              <div className={`text-lg font-bold ${
                p.status === 'exceeded' || p.status === 'critical' ? 'text-red-700' :
                p.status === 'warning' ? 'text-amber-700' : 'text-emerald-700'
              }`}>
                {p.breachDate}
              </div>
              {p.monthsUntilBreach != null && p.monthsUntilBreach > 0 && (
                <div className="text-xs text-slate-500 mt-1">{p.monthsUntilBreach} months</div>
              )}
            </div>
          ))}
        </div>
        {primaryProjection && primaryProjection.status !== 'safe' && (
          <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-800">
            ⚠️ At the current growth rate of <strong>+{currentState.monthlyGrowthRate} items/month</strong>, the team will exceed {threshold} items/person/day by <strong>{primaryProjection.breachDate}</strong>. Consider hiring ahead of this date.
          </div>
        )}
      </div>

      {/* ═══ Monthly Workload Trend ═══ */}
      <div className="bg-white rounded-lg border border-border p-5 mb-6">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Monthly Workload Trend</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b-2 border-border">
              <th className="text-left py-2 px-2 font-medium text-slate-600">Month</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Tickets</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Calls</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Total</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Per Person/Day</th>
              <th className="text-left py-2 px-2 font-medium text-slate-600">Load</th>
            </tr></thead>
            <tbody>
              {monthlyWorkload.map(m => {
                const max = Math.max(...monthlyWorkload.map(x => x.totalWorkItems), 1);
                return (
                  <tr key={m.month} className="border-b border-border-subtle">
                    <td className="py-2 px-2 font-medium">{m.month}</td>
                    <td className="py-2 px-2 text-right">{m.tickets.toLocaleString()}</td>
                    <td className="py-2 px-2 text-right">{m.calls.toLocaleString()}</td>
                    <td className="py-2 px-2 text-right font-medium">{m.totalWorkItems.toLocaleString()}</td>
                    <td className={`py-2 px-2 text-right font-bold ${m.workItemsPerAnalystPerDay > 20 ? 'text-red-600' : m.workItemsPerAnalystPerDay > 15 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {m.workItemsPerAnalystPerDay}
                    </td>
                    <td className="py-2 px-2">
                      <div className="w-full h-4 bg-slate-100 rounded overflow-hidden">
                        <div className={`h-full rounded ${m.workItemsPerAnalystPerDay > 20 ? 'bg-red-400' : m.workItemsPerAnalystPerDay > 15 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                          style={{ width: `${(m.totalWorkItems / max) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Headcount Recommendations ═══ */}
      <div className="bg-white rounded-lg border border-border p-5 mb-6">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Headcount Recommendations</h3>
        <p className="text-xs text-slate-500 mb-4">How many analysts are needed at different capacity thresholds?</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="border-b-2 border-border">
              <th className="text-left py-2 px-2 font-medium text-slate-600">Threshold</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Current</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Needed Now</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Next Quarter</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">6 Months</th>
              <th className="text-right py-2 px-2 font-medium text-slate-600">Surplus/Deficit</th>
            </tr></thead>
            <tbody>
              {headcountRecommendations.map(h => (
                <tr key={h.threshold} className="border-b border-border-subtle">
                  <td className="py-2 px-2">{h.threshold} items/person/day</td>
                  <td className="py-2 px-2 text-right">{h.currentHeadcount}</td>
                  <td className="py-2 px-2 text-right font-medium">{h.neededNow}</td>
                  <td className="py-2 px-2 text-right">{h.neededNextQuarter}</td>
                  <td className="py-2 px-2 text-right">{h.needed6Months}</td>
                  <td className={`py-2 px-2 text-right font-bold ${h.surplusDeficit < 0 ? 'text-red-600' : h.surplusDeficit === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {h.surplusDeficit > 0 ? '+' : ''}{h.surplusDeficit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* ═══ Seasonal Factors ═══ */}
        {seasonalFactors.length > 0 && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Seasonal Load Factors</h3>
            <p className="text-xs text-slate-500 mb-3">1.0 = average month. Higher = busier than normal.</p>
            <div className="space-y-2">
              {seasonalFactors.map(s => (
                <div key={s.month} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 w-16">{s.month}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full rounded ${s.factor > 1.1 ? 'bg-red-400' : s.factor < 0.9 ? 'bg-blue-300' : 'bg-emerald-400'}`}
                      style={{ width: `${Math.min(s.factor * 50, 100)}%` }} />
                  </div>
                  <span className={`text-xs font-medium w-10 text-right ${s.factor > 1.1 ? 'text-red-600' : ''}`}>{s.factor.toFixed(2)}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ Workload Breakdown ═══ */}
        {workloadBreakdown && (
          <div className="bg-white rounded-lg border border-border p-5">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Workload Composition (Last 3 Months)</h3>
            <p className="text-xs text-slate-500 mb-3">What types of work are driving volume?</p>
            <div className="space-y-3">
              {[
                { label: 'General Support', value: workloadBreakdown.generalSupport, color: 'bg-slate-400' },
                { label: 'New Hires', value: workloadBreakdown.newHires, color: 'bg-emerald-400' },
                { label: 'Terminations', value: workloadBreakdown.terminations, color: 'bg-red-400' },
                { label: 'Computer Deployments', value: workloadBreakdown.computerDeploys, color: 'bg-blue-400' },
                { label: 'OF Device Swaps', value: workloadBreakdown.ofSwaps, color: 'bg-amber-400' },
              ].sort((a, b) => b.value - a.value).map(item => {
                const total = (workloadBreakdown.generalSupport + workloadBreakdown.newHires + workloadBreakdown.terminations + workloadBreakdown.computerDeploys + workloadBreakdown.ofSwaps) || 1;
                return (
                  <div key={item.label} className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 w-36">{item.label}</span>
                    <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                      <div className={`h-full rounded ${item.color}`} style={{ width: `${(item.value / total) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium w-16 text-right">{item.value.toLocaleString()} ({Math.round((item.value / total) * 100)}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: 'red' }) {
  return (
    <div className="bg-white rounded-lg border border-border p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color === 'red' ? 'text-red-700' : 'text-text-primary'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
